import { useState, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { apiService } from '../../../services/api';
import type { FaqCategory, FaqItem, AllSymptomsItem } from '../../../types';
import { markdownToCellValue } from '../utils/markdownToRichText';

/** 列幅（文字数）からピクセル近似の行高さを計算するためのヘルパー */
function estimateRowHeight(texts: string[], columnWidths: number[]): number {
  let maxLines = 1;
  for (let i = 0; i < texts.length; i++) {
    const text = texts[i];
    if (!text) continue;
    const width = columnWidths[i] ?? 15;
    // 日本語は全角文字が多いため、幅の約半分の文字数で折り返しと推定
    const charsPerLine = Math.floor(width * 0.8);
    const lines = Math.ceil(text.length / Math.max(charsPerLine, 1));
    if (lines > maxLines) maxLines = lines;
  }
  return Math.max(20, maxLines * 18);
}

const HEADER_STYLE: Partial<ExcelJS.Style> = {
  font: { bold: true, color: { argb: 'FFFFFFFF' } },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
  alignment: { wrapText: true, vertical: 'middle' },
};

/** 1-based column index を Excel の列文字に変換（例: 1→A, 26→Z, 27→AA） */
function colIndexToLetter(col: number): string {
  let result = '';
  let n = col;
  while (n > 0) {
    n--;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

const DATA_ALIGNMENT: Partial<ExcelJS.Alignment> = {
  wrapText: true,
  vertical: 'top',
};

const FAQ_COL_WIDTHS = [15, 15, 30, 50, 14];
const SYMPTOM_COL_WIDTHS = [15, 30, 50, 50];

export type ExportMode = 'template' | 'export';
const TEMPLATE_SAMPLE_COUNT = 3;

/**
 * カテゴリとFAQデータの整合性をチェックし、問題があれば警告メッセージを返す。
 */
export function checkCategoryIntegrity(categories: FaqCategory[], faqs: FaqItem[]): string[] {
  const warnings: string[] = [];
  const categoryIds = new Set(categories.map((c) => c.id));

  // 孤立FAQカテゴリ: productCategoryId が設定されているが対応する製品カテゴリがない
  for (const cat of categories) {
    if (cat.productCategoryId && !categoryIds.has(cat.productCategoryId)) {
      warnings.push(`FAQカテゴリ「${cat.name}」の親製品カテゴリが見つかりません`);
    }
  }

  // 孤立FAQアイテム: faqCategoryId が設定されているが対応するカテゴリがない
  for (const faq of faqs) {
    if (faq.faqCategoryId && !categoryIds.has(faq.faqCategoryId)) {
      warnings.push(`FAQ「${faq.question}」の所属カテゴリが見つかりません`);
    }
  }

  return warnings;
}

export function buildExportWorkbook(
  categories: FaqCategory[],
  faqs: FaqItem[],
  symptoms: AllSymptomsItem[],
  mode: ExportMode = 'export',
): ExcelJS.Workbook {
  const productCategories = categories.filter((c) => !c.productCategoryId);
  const faqCategories = categories.filter((c) => c.productCategoryId);

  // カテゴリ ID → 名前のマップ
  const categoryNameMap = new Map<string, string>();
  for (const cat of [...productCategories, ...faqCategories]) {
    categoryNameMap.set(cat.id, cat.name);
  }
  // FAQカテゴリ ID → 製品カテゴリ名のマップ
  const faqToProductNameMap = new Map<string, string>();
  for (const sub of faqCategories) {
    if (sub.productCategoryId) {
      faqToProductNameMap.set(sub.id, categoryNameMap.get(sub.productCategoryId) ?? '');
    }
  }

  const wb = new ExcelJS.Workbook();

  // --- FAQ シート ---
  const faqSheet = wb.addWorksheet('FAQ');
  const faqHeaders = ['製品カテゴリ', 'FAQカテゴリ', '質問', '回答', 'よくある質問に表示'];

  // 列幅設定
  faqSheet.columns = faqHeaders.map((header, i) => ({
    header,
    key: header,
    width: FAQ_COL_WIDTHS[i],
  }));

  // ヘッダースタイル
  const faqHeaderRow = faqSheet.getRow(1);
  faqHeaderRow.eachCell((cell) => {
    cell.style = HEADER_STYLE as ExcelJS.Style;
  });

  faqSheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0, topLeftCell: 'A2', activeCell: 'A2' }];

  // データ行
  const sortedFaqs = [...faqs].sort((a, b) => a.sortOrder - b.sortOrder);
  const faqsToRender =
    mode === 'template' ? sortedFaqs.slice(0, TEMPLATE_SAMPLE_COUNT) : sortedFaqs;
  for (const faq of faqsToRender) {
    const faqCategoryName = categoryNameMap.get(faq.faqCategoryId) ?? '';
    const productCategoryName = faqToProductNameMap.get(faq.faqCategoryId) ?? '';
    const rowValues = [
      productCategoryName,
      faqCategoryName,
      faq.question,
      '', // answer is set as RichText below
      faq.isInitial ? '○' : '',
    ];
    const row = faqSheet.addRow(rowValues);
    if (faq.answer) {
      row.getCell(4).value = markdownToCellValue(faq.answer);
    }
    row.eachCell((cell) => {
      cell.alignment = DATA_ALIGNMENT;
    });
    row.height = estimateRowHeight(
      [productCategoryName, faqCategoryName, faq.question, faq.answer, faq.isInitial ? '○' : ''],
      FAQ_COL_WIDTHS,
    );
  }

  // --- 症状シート ---
  const symptomSheet = wb.addWorksheet('症状');
  const symptomHeaders = ['製品カテゴリ', '症状名', '原因', '対処法'];

  symptomSheet.columns = symptomHeaders.map((header, i) => ({
    header,
    key: header,
    width: SYMPTOM_COL_WIDTHS[i],
  }));

  const symptomHeaderRow = symptomSheet.getRow(1);
  symptomHeaderRow.eachCell((cell) => {
    cell.style = HEADER_STYLE as ExcelJS.Style;
  });

  symptomSheet.views = [
    { state: 'frozen', ySplit: 1, xSplit: 0, topLeftCell: 'A2', activeCell: 'A2' },
  ];

  const sortedSymptoms = [...symptoms].sort((a, b) => {
    const catNameA = a.categoryName ?? categoryNameMap.get(a.categoryId) ?? '';
    const catNameB = b.categoryName ?? categoryNameMap.get(b.categoryId) ?? '';
    if (catNameA !== catNameB) return catNameA.localeCompare(catNameB);
    return a.symptomName.localeCompare(b.symptomName);
  });
  const symptomsToRender =
    mode === 'template' ? sortedSymptoms.slice(0, TEMPLATE_SAMPLE_COUNT) : sortedSymptoms;

  for (const s of symptomsToRender) {
    const catName = s.categoryName ?? categoryNameMap.get(s.categoryId) ?? '';
    const rowValues = [catName, s.symptomName, '', ''];
    const row = symptomSheet.addRow(rowValues);
    if (s.cause) {
      row.getCell(3).value = markdownToCellValue(s.cause);
    }
    if (s.solution) {
      row.getCell(4).value = markdownToCellValue(s.solution);
    }
    row.eachCell((cell) => {
      cell.alignment = DATA_ALIGNMENT;
    });
    row.height = estimateRowHeight(
      [catName, s.symptomName, s.cause, s.solution],
      SYMPTOM_COL_WIDTHS,
    );
  }

  // --- カテゴリマスタシート（veryHidden） ---
  const masterSheet = wb.addWorksheet('_カテゴリマスタ');
  masterSheet.state = 'veryHidden';

  // A列: 製品カテゴリ名一覧
  const parentNames = productCategories.map((c) => c.name);
  parentNames.forEach((name, i) => {
    masterSheet.getCell(i + 1, 1).value = name;
  });

  // B列以降: 各製品カテゴリのFAQカテゴリを縦に列挙
  // Named Range 用のマッピング（カテゴリ名に使えない文字をアンダースコアに置換）
  // Named Range 名として使用するため、Excel で無効な文字をアンダースコアに置換する。
  // 注意: INDIRECT 関数はセル値（元のカテゴリ名）と Named Range 名の一致を前提とする。
  // 日本語（ひらがな・カタカナ・漢字）はそのまま使用可能だが、スペースや記号を含む
  // カテゴリ名では連動プルダウンが動作しない場合がある（手入力は可能）。
  const sanitizeName = (name: string): string => {
    const safe = name.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF_]/g, '_');
    return /^\d/.test(safe) ? `_${safe}` : safe;
  };

  productCategories.forEach((parent, colIndex) => {
    const children = faqCategories
      .filter((s) => s.productCategoryId === parent.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const col = colIndex + 2; // B列から開始（列2）

    children.forEach((child, rowIndex) => {
      masterSheet.getCell(rowIndex + 1, col).value = child.name;
    });

    // Named Range を定義
    if (children.length > 0) {
      const safeName = sanitizeName(parent.name);
      const colLetter = colIndexToLetter(col);
      wb.definedNames.add(
        `'_カテゴリマスタ'!$${colLetter}$1:$${colLetter}$${children.length}`,
        safeName,
      );
    }
  });

  // 製品カテゴリリストの Named Range
  if (parentNames.length > 0) {
    wb.definedNames.add(`'_カテゴリマスタ'!$A$1:$A$${parentNames.length}`, '_productCategories');
  }

  // --- FAQ シートにデータ入力規則を設定 ---
  const faqDataRowStart = 2; // ヘッダー(1) の次
  const faqDataRowEnd = faqDataRowStart + faqsToRender.length + 100; // 余裕を持たせる

  // カテゴリ列（A列）: 製品カテゴリのリスト
  if (parentNames.length > 0) {
    for (let r = faqDataRowStart; r <= faqDataRowEnd; r++) {
      faqSheet.getCell(r, 1).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`'_カテゴリマスタ'!$A$1:$A$${parentNames.length}`],
        promptTitle: 'カテゴリについて',
        prompt: '新規のカテゴリの場合は新しくカテゴリが登録されます',
        showInputMessage: true,
      };
    }
  }

  // FAQカテゴリ列（B列）: INDIRECT で連動
  for (let r = faqDataRowStart; r <= faqDataRowEnd; r++) {
    faqSheet.getCell(r, 2).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`INDIRECT(A${r})`],
      promptTitle: 'カテゴリについて',
      prompt: '新規のカテゴリの場合は新しくカテゴリが登録されます',
      showInputMessage: true,
    };
  }

  // 初期表示列（E列）: ○ or 空白
  for (let r = faqDataRowStart; r <= faqDataRowEnd; r++) {
    faqSheet.getCell(r, 5).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: ['"○,"'],
      promptTitle: 'よくある質問に表示',
      prompt: 'よくある質問として表示する場合は、プルダウンから ○ を選択してください',
      showInputMessage: true,
    };
  }

  // 回答列（D列）: リンクの書き方ガイド
  for (let r = faqDataRowStart; r <= faqDataRowEnd; r++) {
    const cell = faqSheet.getCell(r, 4);
    cell.dataValidation = {
      ...cell.dataValidation,
      type: 'custom',
      formulae: ['TRUE'],
      promptTitle: 'リンクの書き方',
      prompt: '[表示テキスト](URL) の形式で入力\n例: [お手入れ方法](https://example.com)',
      showInputMessage: true,
    };
  }

  // --- 症状シートにデータ入力規則を設定 ---
  const symptomDataRowStart = 2;
  const symptomDataRowEnd = symptomDataRowStart + symptomsToRender.length + 100;

  if (parentNames.length > 0) {
    for (let r = symptomDataRowStart; r <= symptomDataRowEnd; r++) {
      symptomSheet.getCell(r, 1).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`'_カテゴリマスタ'!$A$1:$A$${parentNames.length}`],
        promptTitle: 'カテゴリについて',
        prompt: '新規のカテゴリの場合は新しくカテゴリが登録されます',
        showInputMessage: true,
      };
    }
  }

  // 原因列（C列）・対処法列（D列）: リンクの書き方ガイド
  for (let r = symptomDataRowStart; r <= symptomDataRowEnd; r++) {
    symptomSheet.getCell(r, 3).dataValidation = {
      type: 'custom',
      formulae: ['TRUE'],
      promptTitle: 'リンクの書き方',
      prompt: '[表示テキスト](URL) の形式で入力\n例: [お手入れ方法](https://example.com)',
      showInputMessage: true,
    };
    symptomSheet.getCell(r, 4).dataValidation = {
      type: 'custom',
      formulae: ['TRUE'],
      promptTitle: 'リンクの書き方',
      prompt: '[表示テキスト](URL) の形式で入力\n例: [お手入れ方法](https://example.com)',
      showInputMessage: true,
    };
  }

  return wb;
}

export function useExcelExporter() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportWarnings, setExportWarnings] = useState<string[]>([]);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [downloadTemplateError, setDownloadTemplateError] = useState<string | null>(null);

  const exportData = useCallback(async () => {
    setIsExporting(true);
    setExportError(null);
    setExportWarnings([]);
    try {
      const [categories, allFaqs, symptomsResp] = await Promise.all([
        apiService.getCategories(),
        apiService.getAllFaqs(),
        apiService.getAllSymptoms(),
      ]);

      const warnings = checkCategoryIntegrity(categories, allFaqs);
      setExportWarnings(warnings);

      const wb = buildExportWorkbook(categories, allFaqs, symptomsResp.items);

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `faq-symptoms-export-${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'エクスポートに失敗しました');
    } finally {
      setIsExporting(false);
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    setDownloadTemplateError(null);
    try {
      const [categories, allFaqs, symptomsResp] = await Promise.all([
        apiService.getCategories(),
        apiService.getAllFaqs(),
        apiService.getAllSymptoms(),
      ]);

      const wb = buildExportWorkbook(categories, allFaqs, symptomsResp.items, 'template');

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk-upload-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadTemplateError(
        err instanceof Error ? err.message : 'テンプレート生成に失敗しました',
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, []);

  return {
    isExporting,
    exportError,
    exportWarnings,
    exportData,
    isDownloadingTemplate,
    downloadTemplateError,
    downloadTemplate,
  };
}
